// src/auth/gateway-auth.ts
var AUTH_TYPES = {
  internal: {
    description: "Internal authentication for employees and services",
    allowedApplications: ["admin-portal", "qa-portal", "analytics-portal", "dashboard"]
  },
  external: {
    description: "External authentication for end users",
    allowedApplications: ["dashboard", "selfservice-portal"]
  }
};
var APPLICATIONS = {
  dashboard: {
    name: "User Dashboard",
    description: "Main application dashboard for end users",
    type: "external"
  },
  "admin-portal": {
    name: "Admin Portal",
    description: "Internal portal for application management",
    type: "internal"
  },
  "qa-portal": {
    name: "QA Portal",
    description: "Internal portal for quality assurance",
    type: "internal"
  },
  "selfservice-portal": {
    name: "Self-Service Portal",
    description: "Customer-facing self-service administration",
    type: "external"
  },
  "analytics-portal": {
    name: "Analytics Portal",
    description: "Internal analytics and reporting",
    type: "internal"
  }
};
var AUTH_FIELDS = ["org_id", "tenant_id", "user_id", "type", "application"];
var HEALTH_CHECK_SENTINEL = "health-check";
function isValidAuthType(authType) {
  return authType.toLowerCase() in AUTH_TYPES;
}
function isValidApplication(application) {
  return application in APPLICATIONS;
}
function getApplicationType(application) {
  const spec = APPLICATIONS[application];
  if (!spec) throw new Error(`Invalid application: ${application}`);
  return spec.type;
}
function isApplicationAllowedForType(authType, application) {
  if (!isValidAuthType(authType) || !isValidApplication(application)) {
    return false;
  }
  return AUTH_TYPES[authType.toLowerCase()].allowedApplications.includes(
    application
  );
}
function getAllowedApplications(authType) {
  if (!isValidAuthType(authType)) throw new Error(`Invalid auth type: ${authType}`);
  return AUTH_TYPES[authType.toLowerCase()].allowedApplications;
}
function canAccessApplication(authType, application) {
  if (!authType) return false;
  return isApplicationAllowedForType(authType, application);
}

// src/auth/app-registry.ts
var APP_REGISTRY = {
  dashboard: { type: "external", subdomain: "dashboard", basePath: "/", localPort: 3e3 },
  "admin-portal": { type: "internal", subdomain: "admin-portal", basePath: "/", localPort: 3001 },
  "selfservice-portal": { type: "external", subdomain: "selfservice-portal", basePath: "/", localPort: 3002 },
  "analytics-portal": { type: "internal", subdomain: "analytics-portal", basePath: "/", localPort: 3003 },
  // Served at its own subdomain apex (`https://qa-portal.{root}/`); the CloudFront
  // distribution maps the bucket at root, so no `/qa/` mount path.
  "qa-portal": { type: "internal", subdomain: "qa-portal", basePath: "/", localPort: 3004 },
  // Pre-auth login website (separate repo: `authentication-loginpage`).
  // `preAuth` keeps it out of the applications.json drift guard.
  //
  // It is PATH-MOUNTED at `/login` on every app subdomain — there is no `login.`
  // host serving it. `login.authe.io` is Auth0's own custom domain (the
  // `/authorize` endpoint, see PROD_CUSTOM_DOMAIN in auth0-env.ts), and
  // `login.sandbox.authe.io` does not exist at all. This entry used to claim
  // `subdomain: "login"`, which sent every `external-site` login either to
  // Auth0's domain root (prd) or to an unresolvable host (sandbox), and the
  // login client has no callback registered on such a host either way.
  //
  // Defaulting to the dashboard's copy; `resolveLoginUrl` takes an `application`
  // to keep a user on their own subdomain instead.
  login: {
    type: "external",
    subdomain: "dashboard",
    basePath: "/login/",
    localPort: 3005,
    preAuth: true,
    aliasOf: "dashboard"
  },
  // First-login onboarding SPA (separate repo: `dashboard-setup-page`). Served
  // under `dashboard.authe.io/setup`, so it shares the dashboard origin + Auth0
  // client in prod (`aliasOf: "dashboard"`). Registered here purely so its local
  // dev origin (`http://localhost:3006`) is resolvable + `returnTo`-allowed when
  // running the fleet side-by-side; it is not a distinct gateway application.
  setup: { type: "external", subdomain: "dashboard", basePath: "/setup/", localPort: 3006, aliasOf: "dashboard" },
  // The PRIVATE share viewer (separate repo: `share-transactions-graph-private-page`).
  // Same shape as `setup`: a sub-site of the dashboard origin, mounted at
  // `/shared-graphs/private/`, using the dashboard's Auth0 client. Its callback
  // path is registered on the dashboard application in applications.json —
  // without that entry Auth0 rejects the redirect and the viewer never gets
  // past sign-in, which is exactly what it was doing.
  "shared-graphs-private": {
    type: "external",
    subdomain: "dashboard",
    basePath: "/shared-graphs/private/",
    localPort: 3007,
    aliasOf: "dashboard"
  }
};
var LOGIN_APP = "login";
var RETURN_TO_PARAM = "returnTo";
function getAppEntry(application) {
  const entry = APP_REGISTRY[application];
  if (!entry) throw new Error(`Unknown application: ${application}`);
  return entry;
}
function resolveAppOrigin(application, ctx) {
  const entry = getAppEntry(application);
  return ctx.local ? `http://localhost:${entry.localPort}` : `https://${entry.subdomain}.${ctx.rootDomain}`;
}
function resolveAppBasePath(application, ctx) {
  return ctx?.local ? "/" : getAppEntry(application).basePath;
}
function resolveAppUrl(target, ctx) {
  const origin = resolveAppOrigin(target.application, ctx);
  const basePath = resolveAppBasePath(target.application, ctx);
  const base = basePath.replace(/\/+$/, "");
  const path = target.path ?? "";
  if (!path || path === "/") return `${origin}${basePath}`;
  const rel = path ? path.startsWith("/") ? path : `/${path}` : "";
  const full = `${base}${rel}`;
  return `${origin}${full === "" ? "/" : full}`;
}
function resolveLoginUrl(options, ctx) {
  const host = options.application ?? LOGIN_APP;
  const entry = APP_REGISTRY[host];
  const url = new URL(
    entry && host !== LOGIN_APP ? resolveAppUrl({ application: host, path: "/login/" }, ctx) : resolveAppUrl({ application: LOGIN_APP }, ctx)
  );
  if (options.returnTo) url.searchParams.set(RETURN_TO_PARAM, options.returnTo);
  for (const [key, value] of Object.entries(options.params ?? {})) {
    if (value != null && value !== "") url.searchParams.set(key, value);
  }
  return url.toString();
}
function isAllowedReturnTo(returnTo, ctx) {
  if (!returnTo) return false;
  if (returnTo.startsWith("/") && !returnTo.startsWith("//")) return true;
  let url;
  try {
    url = new URL(returnTo);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  const allowed = new Set(Object.keys(APP_REGISTRY).map((app) => resolveAppOrigin(app, ctx)));
  return allowed.has(url.origin);
}
function readReturnTo(search, ctx, fallback) {
  const raw = new URLSearchParams(search).get(RETURN_TO_PARAM);
  return raw && isAllowedReturnTo(raw, ctx) ? raw : fallback;
}
function captureReturnTo(loc) {
  const here = loc ?? (typeof window !== "undefined" ? window.location : void 0);
  return here?.href;
}

// src/auth/prefetch.ts
var APP_PREFETCH = {
  // dashboard is the default external destination — warm its entry document.
  dashboard: { documents: ["/"] }
};
function platformPreconnect(ctx) {
  const hosts = /* @__PURE__ */ new Set();
  hosts.add(`https://api.${ctx.rootDomain}`);
  if (ctx.auth0Domain) hosts.add(`https://${ctx.auth0Domain}`);
  for (const h of ctx.extraPreconnect ?? []) hosts.add(h);
  return [...hosts];
}
function resolveAppPrefetch(application, ctx) {
  const origin = resolveAppOrigin(application, ctx);
  const hints = APP_PREFETCH[application] ?? {};
  const docs = hints.documents ?? ["/"];
  return {
    preconnect: [origin, ...hints.preconnect ?? []],
    documents: docs.map((path) => resolveAppUrl({ application, path }, ctx)),
    modules: (hints.modules ?? []).map((m) => `${origin}${m.startsWith("/") ? m : `/${m}`}`)
  };
}
function buildPrefetchPlan(opts) {
  const preconnect = /* @__PURE__ */ new Set();
  const documents = /* @__PURE__ */ new Set();
  const modules = /* @__PURE__ */ new Set();
  if (opts.includePlatform !== false) {
    for (const host of platformPreconnect(opts.ctx)) preconnect.add(host);
  }
  if (opts.returnTo) {
    try {
      const url = new URL(opts.returnTo);
      preconnect.add(url.origin);
      documents.add(url.toString());
    } catch {
    }
  }
  const apps = opts.apps ?? Object.keys(APP_REGISTRY).filter((app) => {
    const entry = APP_REGISTRY[app];
    return app !== LOGIN_APP && !entry.preAuth && !entry.aliasOf;
  });
  for (const app of apps) {
    if (!APP_REGISTRY[app]) continue;
    if (getAppEntry(app).type === "internal" && !opts.includeInternal) continue;
    const plan = resolveAppPrefetch(app, opts.ctx);
    for (const p of plan.preconnect) preconnect.add(p);
    for (const d of plan.documents) documents.add(d);
    for (const m of plan.modules) modules.add(m);
  }
  return { preconnect: [...preconnect], documents: [...documents], modules: [...modules] };
}
function applyPrefetchPlan(plan, doc) {
  const target = doc ?? (typeof document !== "undefined" ? document : void 0);
  if (!target) return () => {
  };
  const head = target.head ?? target.getElementsByTagName("head")[0];
  if (!head) return () => {
  };
  const created = [];
  const add = (rel, href, as, crossorigin) => {
    const selector = `link[rel="${rel}"][href="${href}"]`;
    if (head.querySelector(selector)) return;
    const link = target.createElement("link");
    link.rel = rel;
    link.href = href;
    if (as) link.setAttribute("as", as);
    if (crossorigin) link.crossOrigin = "anonymous";
    head.appendChild(link);
    created.push(link);
  };
  for (const origin of plan.preconnect) {
    add("preconnect", origin, void 0, true);
    add("dns-prefetch", origin);
  }
  for (const document_ of plan.documents) add("prefetch", document_, "document");
  for (const moduleUrl of plan.modules) add("modulepreload", moduleUrl);
  return () => {
    for (const el of created) el.remove();
  };
}

// src/auth/jwt-claims.ts
var DEFAULT_CLAIMS_NAMESPACE = "https://authe.io/";
function findNamespacedClaim(payload, key) {
  const suffix = "/" + key;
  for (const k of Object.keys(payload)) {
    if (k.includes("://") && k.endsWith(suffix)) return payload[k];
  }
  return void 0;
}
function createClaimReader(namespace = DEFAULT_CLAIMS_NAMESPACE) {
  const ns = namespace.endsWith("/") ? namespace : namespace + "/";
  function readClaim(payload, key) {
    if (!payload) return void 0;
    const direct = payload[ns + key];
    if (direct !== void 0) return direct;
    const bare = payload[key];
    if (bare !== void 0) return bare;
    return findNamespacedClaim(payload, key);
  }
  function readAll(payload) {
    if (!payload) return {};
    const out = {};
    const fields = [
      "customer_id",
      "org_id",
      "tenant_id",
      "user_id",
      "customer_name",
      "org_name",
      "tenant_name",
      "user_name",
      "type",
      "application",
      "roles",
      "scopes",
      "role_permissions",
      "policy_version",
      "plan_id",
      "qa_config",
      "feature_flags",
      "region",
      "region_config",
      "continent",
      "flow_type",
      "is_demo",
      "invited_by",
      "api_key_id",
      "auth_provider",
      "is_service",
      "act",
      "impersonated",
      "impersonation_id",
      "impersonation_reason",
      "impersonation_access_level",
      "impersonation_expires_at"
    ];
    for (const field of fields) {
      const val = readClaim(payload, field);
      if (val !== void 0) {
        out[field] = val;
      }
    }
    return out;
  }
  return { readClaim, readAll, namespace: ns };
}
var claimReader = createClaimReader();
function getScopes(payload, namespace = DEFAULT_CLAIMS_NAMESPACE) {
  return createClaimReader(namespace).readClaim(payload, "scopes") ?? [];
}
function getRegionConfig(payload, namespace = DEFAULT_CLAIMS_NAMESPACE) {
  return createClaimReader(namespace).readClaim(payload, "region_config") ?? void 0;
}
function decodeBase64Url(segment) {
  const b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.padEnd(b64.length + (4 - b64.length % 4) % 4, "=");
  if (typeof atob === "function") {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  return Buffer.from(padded, "base64").toString("utf8");
}
function decodeJwtPayload(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const parsed = JSON.parse(decodeBase64Url(parts[1]));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// src/auth/dev-auth.ts
function createMockToken(input = {}, namespace = DEFAULT_CLAIMS_NAMESPACE) {
  const ns = namespace.endsWith("/") ? namespace : `${namespace}/`;
  const { sub, email, ttlSeconds, ...claims } = input;
  const now = Math.floor(Date.now() / 1e3);
  const payload = {
    sub: sub ?? "auth0|local-dev-user",
    email: email ?? "dev@localhost",
    iss: ns,
    iat: now,
    exp: now + (ttlSeconds ?? 3600)
  };
  for (const [key, value] of Object.entries(claims)) {
    if (value !== void 0) payload[ns + key] = value;
  }
  return payload;
}
function isDevAuthEnabled(opts = {}) {
  if (opts.env === "prd") return false;
  const search = opts.search ?? (typeof window !== "undefined" ? window.location.search : "");
  const value = new URLSearchParams(search).get("devauth");
  return value != null && value !== "0" && value !== "false";
}

// src/auth/impersonation.ts
var INACTIVE = {
  active: false,
  actorSub: null,
  targetSub: null,
  sessionId: null,
  reason: null,
  expiresAt: null,
  expired: false,
  accessLevel: "read_only"
};
function expiryPassed(expiresAt, now) {
  if (!expiresAt) return false;
  const ts = Date.parse(expiresAt);
  return Number.isFinite(ts) && ts <= now;
}
function getImpersonationState(claims, options = {}) {
  const actorSub = claims?.act?.sub ?? null;
  if (!claims || claims.impersonated !== true || !actorSub) return INACTIVE;
  const now = options.now ?? Date.now();
  const expiresAt = claims.impersonation_expires_at ?? null;
  return {
    active: true,
    actorSub,
    targetSub: options.targetSub ?? null,
    sessionId: claims.impersonation_id ?? null,
    reason: claims.impersonation_reason ?? null,
    expiresAt,
    expired: expiryPassed(expiresAt, now),
    // Anything unrecognised reads as read_only, matching the backend: a
    // malformed claim must never be the thing that implies write access.
    accessLevel: claims.impersonation_access_level === "read_write" ? "read_write" : "read_only"
  };
}
function isImpersonating(claims) {
  return claims?.impersonated === true && !!claims?.act?.sub;
}
function isImpersonationExpired(claims, now = Date.now()) {
  if (!isImpersonating(claims)) return false;
  return expiryPassed(claims?.impersonation_expires_at, now);
}
function impersonationBanner(claims, options = {}) {
  const state = getImpersonationState(claims, options);
  if (!state.active) return null;
  const who = state.targetSub ? `user ${state.targetSub}` : "another user";
  const mode = state.accessLevel === "read_write" ? "read/write" : "read-only";
  const message = state.expired ? `Impersonation session for ${who} has expired \u2014 re-authenticate to exit.` : `You are acting as ${who}, ${mode} (impersonated by ${state.actorSub}).`;
  return {
    message,
    actorSub: state.actorSub,
    targetSub: state.targetSub,
    sessionId: state.sessionId,
    reason: state.reason,
    expiresAt: state.expiresAt,
    expired: state.expired,
    accessLevel: state.accessLevel
  };
}

// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var GLOBAL_TENANT_COMPOSITE = "main-main";

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/tenancy/index.ts
var TenantContext = class _TenantContext {
  static {
    brandClass(this, "tenancy/TenantContext");
  }
  customerId;
  orgId;
  tenantId;
  userId;
  constructor(init = {}) {
    this.customerId = init.customerId ?? null;
    this.orgId = init.orgId || GLOBAL_TENANT;
    this.tenantId = init.tenantId || GLOBAL_TENANT;
    this.userId = init.userId ?? null;
  }
  /** Composite key `{org_id}-{tenant_id}` used in DDB SKs + global-tier checks. */
  get orgIdTenantId() {
    return `${this.orgId}-${this.tenantId}`;
  }
  get isGlobal() {
    return this.orgIdTenantId === GLOBAL_TENANT_COMPOSITE;
  }
  get isUserScoped() {
    return this.userId != null && this.userId.length > 0;
  }
  get hasCustomerScope() {
    return this.customerId != null && this.customerId.length > 0 || this.orgId !== GLOBAL_TENANT;
  }
  withUser(userId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId
    });
  }
  withOrg(orgId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  withTenant(tenantId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId,
      userId: this.userId
    });
  }
  withCustomer(customerId) {
    return new _TenantContext({
      customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  toJSON() {
    return {
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId,
      orgIdTenantId: this.orgIdTenantId
    };
  }
};

// src/auth/auth0.ts
function buildAuth0Config(env) {
  return {
    domain: env.domain,
    clientId: env.clientId,
    authorizationParams: {
      redirect_uri: env.redirectUri ?? (typeof window !== "undefined" ? window.location.origin : void 0),
      audience: env.audience,
      scope: env.scope ?? "openid profile email offline_access",
      organization: env.organization
    },
    claimsNamespace: env.claimsNamespace ?? "https://authe.io/"
  };
}
function tenantContextFromClaims(payload, claimsNamespace = "https://authe.io/") {
  const reader = createClaimReader(claimsNamespace);
  const claims = reader.readAll(payload);
  return new TenantContext({
    customerId: claims.customer_id ?? null,
    orgId: claims.org_id ?? null,
    tenantId: claims.tenant_id ?? null,
    userId: claims.user_id ?? null
  });
}
var EMPTY_TENANCY_NAMES = {
  customerName: null,
  orgName: null,
  tenantName: null,
  userName: null
};
function nonEmptyName(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
function tenancyNamesFromClaims(payload, claimsNamespace = "https://authe.io/") {
  const claims = createClaimReader(claimsNamespace).readAll(payload);
  return {
    customerName: nonEmptyName(claims.customer_name),
    orgName: nonEmptyName(claims.org_name),
    tenantName: nonEmptyName(claims.tenant_name),
    userName: nonEmptyName(claims.user_name)
  };
}
function readAutheClaims(payload, claimsNamespace = "https://authe.io/") {
  return createClaimReader(claimsNamespace).readAll(payload);
}
function tenantStoragePrefix(opts) {
  return `${opts.platform}-${opts.env}-${opts.sub}-${opts.orgId}-${opts.tenantId}`;
}
function storage(opts) {
  if (opts.getStorage) return opts.getStorage();
  return typeof localStorage !== "undefined" ? localStorage : void 0;
}
function pinRegion(alias, opts) {
  storage(opts)?.setItem(`${opts.prefix}_use_region_alias`, JSON.stringify(alias));
}
function readRegionPin(opts) {
  try {
    return JSON.parse(storage(opts)?.getItem(`${opts.prefix}_use_region_alias`) ?? '"default"');
  } catch {
    return "default";
  }
}
function clearRegionPin(opts) {
  storage(opts)?.removeItem(`${opts.prefix}_use_region_alias`);
}
async function switchOrganization(client, organization) {
  await client.loginWithRedirect({ authorizationParams: { organization, prompt: "login" } });
}
async function switchTenant(client, tenantId, options = {}) {
  const authorizationParams = { tenant_id: tenantId };
  try {
    await client.getAccessTokenSilently({ cacheMode: "off", authorizationParams });
  } catch (error) {
    if (!client.loginWithRedirect) throw error;
    await client.loginWithRedirect({ authorizationParams });
    return;
  }
  const reload = options.reload ?? (() => {
    if (typeof window !== "undefined") window.location.reload();
  });
  reload();
}

// src/auth/auth0-env.ts
var AUTH0_TENANTS = {
  sandbox: {
    domain: "tenant22r23rfwefwfe.us.auth0.com",
    clientId: "nuDvszbwYz0z4qL1pmpIKPMpwqvwMowl"
  },
  staging: {
    domain: "tenant22r23rfwefwfe.us.auth0.com",
    clientId: "nuDvszbwYz0z4qL1pmpIKPMpwqvwMowl"
  },
  prd: {
    domain: "dev-8drlzxonkeqsd0jf.us.auth0.com",
    clientId: "h9F98oKMdCT5vHAwN1mf1izd6Z8Z4kf1"
  }
};
var PROD_CUSTOM_DOMAIN = "login.authe.io";
function auth0TenantEnv(env) {
  if (env === "prd") return "prd";
  if (env === "staging") return "staging";
  return "sandbox";
}
function resolveAuth0Config(options) {
  const tenant = AUTH0_TENANTS[auth0TenantEnv(options.env)];
  const overrides = options.overrides ?? {};
  const domain = overrides.domain ?? (options.env === "prd" && options.useCustomDomain ? PROD_CUSTOM_DOMAIN : tenant.domain);
  return {
    ...overrides,
    domain,
    clientId: overrides.clientId ?? tenant.clientId,
    audience: overrides.audience ?? `https://api.${options.rootDomain}`,
    // Env-specific claim namespace = `https://{rootDomain}/`, matching the
    // post-login Action's emitted namespace AND the backend authorizer's
    // DomainName (`https://sandbox.authe.io` in sandbox, `https://authe.io` in
    // prd). Reading claims under any other namespace yields `undefined`.
    claimsNamespace: overrides.claimsNamespace ?? `https://${options.rootDomain}/`
  };
}
function resolveAuth0ConfigFromEnv(env, options = {}) {
  return resolveAuth0Config({
    env: env.env,
    rootDomain: env.apiBaseDomain,
    useCustomDomain: options.useCustomDomain,
    // claimsNamespace is derived from rootDomain inside resolveAuth0Config
    // (`https://{rootDomain}/`) so it tracks the env-specific namespace the
    // post-login Action emits and the backend authorizer expects.
    overrides: {
      domain: env.auth0Domain,
      clientId: env.auth0ClientId,
      audience: env.auth0Audience,
      ...options.overrides
    }
  });
}

// src/auth/action-vocabulary.ts
var MANAGE_ACTION = "*";
var DEFAULT_SCOPE_ACTIONS = ["list", "show", "create", "edit", "delete"];
var ACTION_TO_SCOPE_ACTIONS = {
  read: ["list", "show"],
  list: ["list"],
  show: ["show"],
  create: ["create"],
  update: ["edit"],
  edit: ["edit"],
  delete: ["delete"],
  export: ["export"],
  invoke: ["invoke"],
  [MANAGE_ACTION]: DEFAULT_SCOPE_ACTIONS
};
function scopeActionsFor(action) {
  const key = action.trim().toLowerCase();
  return ACTION_TO_SCOPE_ACTIONS[key] ?? [key];
}
function actionsGrant(actions, wanted) {
  if (!actions || actions.length === 0) return false;
  if (actions.includes(MANAGE_ACTION)) return true;
  const target = new Set(scopeActionsFor(wanted));
  for (const held of actions) {
    for (const granted of scopeActionsFor(held)) {
      if (target.has(granted)) return true;
    }
  }
  return false;
}
function toScopeActions(matrix) {
  const out = {};
  for (const [group, actions] of Object.entries(matrix ?? {})) {
    if (actions?.includes(MANAGE_ACTION)) {
      out[group] = [MANAGE_ACTION];
      continue;
    }
    const expanded = /* @__PURE__ */ new Set();
    for (const action of actions ?? []) for (const scope of scopeActionsFor(action)) expanded.add(scope);
    out[group] = [...expanded];
  }
  return out;
}
var TEAM_GROUP_SCOPES = {
  team: {
    read: "teams:read",
    list: "teams:list",
    show: "teams:read",
    create: "teams:create",
    update: "teams:update",
    edit: "teams:update",
    delete: "teams:delete"
  },
  team_members: {
    read: "team-members:read",
    list: "team-members:list",
    show: "team-members:read",
    create: "team-members:create",
    update: "team-members:update",
    edit: "team-members:update",
    delete: "team-members:delete"
  },
  team_roles: {
    read: "team-roles:read",
    list: "team-roles:list",
    show: "team-roles:read",
    create: "team-roles:create",
    update: "team-roles:update",
    edit: "team-roles:update",
    delete: "team-roles:delete"
  },
  team_invites: {
    read: "team-invites:read",
    list: "team-invites:list",
    show: "team-invites:read",
    // An invite is sent and revoked, not created and deleted.
    create: "team-invites:send",
    update: "team-invites:resend",
    edit: "team-invites:resend",
    delete: "team-invites:revoke"
  },
  // Read-only by nature: there is no write verb to map.
  team_audit: {
    read: "team-audit:read",
    list: "team-audit:read",
    show: "team-audit:read"
  }
};
function teamScopeFor(group, action) {
  return TEAM_GROUP_SCOPES[group.trim()]?.[action.trim().toLowerCase()];
}
function splitScope(scope) {
  const i = scope.lastIndexOf(":");
  if (i <= 0 || i === scope.length - 1) return null;
  return [scope.slice(0, i), scope.slice(i + 1)];
}
function rolePermissionsGrant(rolePermissions, resource, action) {
  if (!rolePermissions) return false;
  if (actionsGrant(rolePermissions[resource], action)) return true;
  const scope = teamScopeFor(resource, action);
  if (!scope) return false;
  const parsed = splitScope(scope);
  if (!parsed) return false;
  const held = rolePermissions[parsed[0]];
  if (!held || held.length === 0) return false;
  return held.includes(MANAGE_ACTION) || held.includes(parsed[1]);
}

// src/async/singleflight.ts
function singleflight() {
  const inflight = /* @__PURE__ */ new Map();
  return {
    run(key, fn) {
      const existing = inflight.get(key);
      if (existing) return existing;
      const promise = Promise.resolve().then(fn);
      inflight.set(key, promise);
      const clear = () => {
        if (inflight.get(key) === promise) inflight.delete(key);
      };
      promise.then(clear, clear);
      return promise;
    },
    forget(key) {
      inflight.delete(key);
    },
    get size() {
      return inflight.size;
    }
  };
}

// src/auth/casbin.ts
var RBAC_MODEL = `
[request_definition]
r = sub, obj, act

[policy_definition]
p = sub, obj, act

[role_definition]
g = _, _

[policy_effect]
e = some(where (p.eft == allow))

[matchers]
m = g(r.sub, p.sub) && keyMatch(r.obj, p.obj) && regexMatch(r.act, p.act)
`;
var DEFAULT_RBAC_POLICY = "";
async function createAutheEnforcer(policyCsv = DEFAULT_RBAC_POLICY) {
  const moduleSpecifier = "casbin.js";
  let casbin;
  try {
    casbin = await import(
      /* @vite-ignore */
      moduleSpecifier
    );
  } catch {
    throw new Error(
      "casbin.js is required for createAutheEnforcer(). Install it, or use enforceFromRolePermissions() for the JWT fast-path."
    );
  }
  const model = casbin.newModel(RBAC_MODEL);
  const adapter = new casbin.MemoryAdapter(policyCsv);
  const enforcer = await casbin.newEnforcer(model, adapter);
  return {
    enforce: (sub, obj, act) => enforcer.enforce(sub, obj, act)
  };
}
function policyFromRolePermissions(role, rolePermissions) {
  const lines = [];
  for (const [resource, actions] of Object.entries(rolePermissions)) {
    for (const action of actions) {
      lines.push(`p, ${role}, ${resource}, (${action})`);
    }
  }
  return lines.join("\n");
}
function enforceFromRolePermissions(rolePermissions, resource, action) {
  return rolePermissionsGrant(rolePermissions, resource, action);
}
var permKey = (resource, action) => `${resource}\0${action}`;
function permissionSetFromRolePermissions(rolePermissions) {
  return { can: (resource, action) => enforceFromRolePermissions(rolePermissions, resource, action) };
}
async function materializePermissionSet(enforcer, subject, grid) {
  const granted = /* @__PURE__ */ new Set();
  await Promise.all(
    grid.resources.flatMap(
      (resource) => grid.actions.map(async (action) => {
        if (await enforcer.enforce(subject, resource, action)) granted.add(permKey(resource, action));
      })
    )
  );
  return { can: (resource, action) => granted.has(permKey(resource, action)) };
}
function keyMatch(key, pattern) {
  const star = pattern.indexOf("*");
  if (star === -1) return key === pattern;
  if (key.length > star) return key.slice(0, star) === pattern.slice(0, star);
  return key === pattern.slice(0, star);
}
function stripParens(act) {
  const m = /^\((.*)\)$/.exec(act.trim());
  return m ? m[1] : act.trim();
}
function actionMatches(action, pattern) {
  if (pattern === "*" || pattern === ".*") return true;
  try {
    return new RegExp(`^(?:${pattern})$`).test(action);
  } catch {
    return action === pattern;
  }
}
function parsePolicyCsv(csv) {
  const grants = [];
  const roleLinks = [];
  for (const raw of csv.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const [ptype, ...rest] = line.split(",").map((p) => p.trim());
    if (ptype === "p" && rest.length >= 3) {
      grants.push({ role: rest[0], resource: rest[1], action: stripParens(rest[2]) });
    } else if (ptype === "g" && rest.length >= 2) {
      roleLinks.push({ child: rest[0], parent: rest[1] });
    }
  }
  return { grants, roleLinks };
}
function parseRolePermissionsPolicy(byRole) {
  const grants = [];
  for (const [role, perms] of Object.entries(byRole)) {
    for (const [resource, actions] of Object.entries(perms)) {
      for (const action of actions) grants.push({ role, resource, action });
    }
  }
  return { grants, roleLinks: [] };
}
function parseRolePermissionsArray(entries) {
  const grants = [];
  for (const entry of entries) {
    const role = entry?.name;
    if (!role || !Array.isArray(entry.scopes)) continue;
    for (const scope of entry.scopes) {
      const parsed = splitScope2(String(scope));
      if (parsed) grants.push({ role, resource: parsed.resource, action: parsed.action });
    }
  }
  return { grants, roleLinks: [] };
}
function expandRoles(roleNames, roleLinks) {
  const result = new Set(roleNames);
  let changed = true;
  while (changed) {
    changed = false;
    for (const { child, parent } of roleLinks) {
      if (result.has(child) && !result.has(parent)) {
        result.add(parent);
        changed = true;
      }
    }
  }
  return result;
}
function permissionSetFromPolicy(roleNames, policy) {
  const roles = expandRoles(roleNames, policy.roleLinks);
  const grants = policy.grants.filter((g) => g.role === "*" || roles.has(g.role));
  return {
    can: (resource, action) => grants.some((g) => keyMatch(resource, g.resource) && actionMatches(action, g.action))
  };
}
async function loadPolicyFromUrl(url, options = {}) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const res = await fetchImpl(url, { cache: "no-cache", signal: options.signal });
  if (!res.ok) throw new Error(`policy fetch failed: ${res.status} ${url}`);
  const text = await res.text();
  const looksCsv = url.endsWith(".csv") || /^\s*[pg]\s*,/m.test(text);
  if (looksCsv) return parsePolicyCsv(text);
  const json = JSON.parse(text);
  if (Array.isArray(json)) return parseRolePermissionsArray(json);
  return parseRolePermissionsPolicy(json);
}
function policyDataEnv(env) {
  return env === "local" ? "sandbox" : env;
}
function policyFileName(env, format) {
  return (format ?? "json") === "csv" ? `policy-${env}.csv` : "role_permissions.json";
}
function resolvePolicyUrl(source) {
  const base = source.baseUrl.replace(/\/+$/, "");
  if (source.objectPath) return `${base}/${source.objectPath.replace(/^\/+/, "")}`;
  const env = policyDataEnv(source.env);
  return `${base}/public/GET/cdn/${env}/global/permissions-data/${policyFileName(env, source.format)}`;
}
var policies = globalSingleton("auth/policies", () => ({
  cache: /* @__PURE__ */ new Map(),
  inflight: singleflight()
}));
async function loadPolicy(source, options = {}) {
  const url = typeof source === "string" ? source : resolvePolicyUrl(source);
  if (options.force) {
    policies.cache.delete(url);
    policies.inflight.forget(url);
  }
  const cached = policies.cache.get(url);
  if (cached) return cached;
  return policies.inflight.run(url, async () => {
    const policy = await loadPolicyFromUrl(url, options);
    policies.cache.set(url, policy);
    return policy;
  });
}
function clearPolicyCache() {
  policies.cache.clear();
}
function permissionSetFromClaims(claims, policy) {
  if (policy) return permissionSetFromPolicy(claims?.roles ?? [], policy);
  return permissionSetFromRolePermissions(claims?.role_permissions);
}
function splitActions(action) {
  const a = action.trim().replace(/^\((.*)\)$/, "$1").trim();
  if (a === "") return [];
  if (a === ".*") return ["*"];
  return a.split("|").map((s) => s.trim()).filter(Boolean);
}
function applicableGrants(roleNames, policy) {
  const roles = expandRoles(roleNames, policy.roleLinks);
  return policy.grants.filter((g) => g.role === "*" || roles.has(g.role));
}
function scopesFromPolicy(roleNames, policy) {
  const out = /* @__PURE__ */ new Set();
  for (const g of applicableGrants(roleNames, policy)) {
    for (const action of splitActions(g.action)) out.add(`${g.resource}:${action}`);
  }
  return [...out].sort();
}
function rolePermissionsFromPolicy(roleNames, policy) {
  const acc = {};
  for (const g of applicableGrants(roleNames, policy)) {
    for (const action of splitActions(g.action)) (acc[g.resource] ??= /* @__PURE__ */ new Set()).add(action);
  }
  return sortedMap(acc);
}
function splitScope2(scope) {
  const i = scope.lastIndexOf(":");
  if (i <= 0 || i === scope.length - 1) return null;
  return { resource: scope.slice(0, i), action: scope.slice(i + 1) };
}
function iterateClaimScopes(claim, onScope) {
  if (Array.isArray(claim)) {
    for (const entry of claim) {
      if (typeof entry === "string") {
        onScope(entry);
        continue;
      }
      const scopes = entry?.scopes;
      if (Array.isArray(scopes)) for (const s of scopes) onScope(String(s));
    }
  } else if (claim && typeof claim === "object") {
    for (const [resource, actions] of Object.entries(claim)) {
      if (Array.isArray(actions)) for (const a of actions) onScope(`${resource}:${a}`);
    }
  }
}
function sortedMap(acc) {
  const out = {};
  for (const key of Object.keys(acc).sort()) out[key] = [...acc[key]].sort();
  return out;
}
function scopesFromRolePermissionsClaim(claim) {
  const out = /* @__PURE__ */ new Set();
  iterateClaimScopes(claim, (s) => out.add(s));
  return [...out].sort();
}
function rolePermissionsMapFromClaim(claim) {
  const acc = {};
  iterateClaimScopes(claim, (scope) => {
    const parsed = splitScope2(scope);
    if (parsed) (acc[parsed.resource] ??= /* @__PURE__ */ new Set()).add(parsed.action);
  });
  return sortedMap(acc);
}
var EMPTY_VIEW = { scopes: [], rolePermissions: {}, source: "none" };
function mergeRolePermissions(a, b) {
  const acc = {};
  for (const map of [a, b]) {
    for (const [resource, actions] of Object.entries(map)) {
      for (const action of actions) (acc[resource] ??= /* @__PURE__ */ new Set()).add(action);
    }
  }
  return sortedMap(acc);
}
function resolvePermissionsView(input) {
  const roles = input.roles ?? [];
  const policyScopes = input.policy ? scopesFromPolicy(roles, input.policy) : [];
  const tokenScopes = scopesFromRolePermissionsClaim(input.rolePermissionsClaim);
  if (!policyScopes.length && !tokenScopes.length) return EMPTY_VIEW;
  return {
    scopes: [.../* @__PURE__ */ new Set([...policyScopes, ...tokenScopes])].sort(),
    rolePermissions: mergeRolePermissions(
      input.policy ? rolePermissionsFromPolicy(roles, input.policy) : {},
      rolePermissionsMapFromClaim(input.rolePermissionsClaim)
    ),
    source: policyScopes.length && tokenScopes.length ? "merged" : policyScopes.length ? "policy" : "token"
  };
}
async function loadPermissionsView(options) {
  let policy = null;
  if (options.policySource) {
    try {
      policy = await loadPolicy(options.policySource, {
        fetchImpl: options.fetchImpl,
        signal: options.signal
      });
    } catch {
      policy = null;
    }
  }
  let rolePermissionsClaim;
  if (options.accessToken) {
    const payload = decodeJwtPayload(options.accessToken);
    if (payload) {
      rolePermissionsClaim = createClaimReader(options.namespace).readClaim(
        payload,
        "role_permissions"
      );
    }
  }
  return resolvePermissionsView({ roles: options.roles, policy, rolePermissionsClaim });
}

// src/auth/scope-catalog.ts
function splitScopeName(name) {
  const i = name.lastIndexOf(":");
  if (i <= 0) return { resource: name, action: "" };
  return { resource: name.slice(0, i), action: name.slice(i + 1) };
}
function resolveScopeCatalogUrl(source) {
  const base = source.baseUrl.replace(/\/+$/, "");
  return `${base}/public/GET/cdn/${source.env}/global/permissions-data/role_permissions_with_scope_description.json`;
}
function parseScopeCatalog(json) {
  const rawRoles = Array.isArray(json) ? json : [];
  const byName = /* @__PURE__ */ new Map();
  const roles = [];
  for (const role of rawRoles) {
    if (!role?.name) continue;
    const scopeNames = [];
    for (const entry of role.scopes ?? []) {
      const name = typeof entry === "string" ? entry : entry?.name;
      if (!name) continue;
      const description = typeof entry === "string" ? "" : entry?.description ?? "";
      scopeNames.push(name);
      const existing = byName.get(name);
      if (!existing || !existing.description && description) {
        const { resource, action } = splitScopeName(name);
        byName.set(name, { name, resource, action, description });
      }
    }
    roles.push({ name: role.name, description: role.description, scopes: scopeNames });
  }
  const scopes = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { scopes, roles };
}
var catalogs = globalSingleton("auth/scope-catalogs", () => ({
  cache: /* @__PURE__ */ new Map(),
  inflight: singleflight()
}));
async function loadScopeCatalog(source, options = {}) {
  const url = typeof source === "string" ? source : resolveScopeCatalogUrl(source);
  if (options.force) {
    catalogs.cache.delete(url);
    catalogs.inflight.forget(url);
  }
  const cached = catalogs.cache.get(url);
  if (cached) return cached;
  return catalogs.inflight.run(url, async () => {
    const fetchImpl = options.fetchImpl ?? fetch;
    const res = await fetchImpl(url, { signal: options.signal });
    if (!res.ok) throw new Error(`scope catalog fetch failed: ${res.status} ${url}`);
    const catalog = parseScopeCatalog(JSON.parse(await res.text()));
    catalogs.cache.set(url, catalog);
    return catalog;
  });
}
function clearScopeCatalogCache() {
  catalogs.cache.clear();
}

// src/env/index.ts
function readEnvKey(source, name) {
  const candidates = [`VITE_${name}`, `REACT_APP_${name}`, name];
  for (const c of candidates) {
    const v = source[c];
    if (v !== void 0 && v !== "") return String(v);
  }
  return void 0;
}

// src/auth/build-time-policy.ts
var BUILD_TIME_POLICY_ENV_KEYS = ["CASBIN_POLICY", "CASBIN_PERMISSION_FROM_SSM"];
function resolveBuildTimePolicyCsv(source = {}, fallback = DEFAULT_RBAC_POLICY) {
  for (const key of BUILD_TIME_POLICY_ENV_KEYS) {
    const value = readEnvKey(source, key);
    if (value) return value;
  }
  return fallback;
}
function parseBuildTimePolicy(source = {}, fallback) {
  return parsePolicyCsv(resolveBuildTimePolicyCsv(source, fallback));
}
function permissionSetFromPolicyCsv(roleNames, csv) {
  return permissionSetFromPolicy(roleNames, parsePolicyCsv(csv));
}
function buildTimePermissionSet(options = {}) {
  return permissionSetFromPolicy(
    options.roles ?? [],
    parseBuildTimePolicy(options.source ?? {}, options.fallback)
  );
}

// src/auth/resource-groups.ts
var TEAM = "team";
var TEAM_MEMBERS = "team_members";
var TEAM_ROLES = "team_roles";
var TEAM_INVITES = "team_invites";
var TEAM_AUDIT = "team_audit";
var TEAM_MANAGEMENT_RESOURCES = [
  TEAM,
  TEAM_MEMBERS,
  TEAM_ROLES,
  TEAM_INVITES,
  TEAM_AUDIT
];
var COLLABORATIVE_RESOURCES = [
  "transactions",
  "addresses",
  "entities",
  "tags",
  "labels",
  "groups",
  "disputes",
  "streams",
  "pipelines",
  "workspaces",
  "notifications"
];
var RESOURCE_GROUP_LABEL = {
  transactions: "Transactions",
  addresses: "Addresses",
  entities: "Entities",
  tags: "Tags",
  labels: "Labels",
  groups: "Groups",
  disputes: "Disputes",
  streams: "Streams",
  pipelines: "Pipelines",
  workspaces: "Workspaces",
  notifications: "Notifications",
  [TEAM]: "Team settings",
  [TEAM_MEMBERS]: "Team members",
  [TEAM_ROLES]: "Roles",
  [TEAM_INVITES]: "Invites",
  [TEAM_AUDIT]: "Audit log"
};
var TEAM_GROUP_DESCRIPTION = {
  [TEAM]: "Rename the team and change its settings.",
  [TEAM_MEMBERS]: "Invite, edit, and remove members.",
  [TEAM_ROLES]: "Create and edit roles + permissions.",
  [TEAM_INVITES]: "Send, resend and revoke invitations.",
  [TEAM_AUDIT]: "View the team activity log."
};

// src/auth/permission-matrix.ts
var DEFAULT_ACTIONS = ["list", "show", "create", "edit", "delete", "export"];
var ACTION_LABEL = {
  list: "List",
  show: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  export: "Export",
  [MANAGE_ACTION]: "Manage"
};
var DEFAULT_RESOURCE_GROUPS = [
  ...COLLABORATIVE_RESOURCES.map((id) => ({ id, label: RESOURCE_GROUP_LABEL[id] ?? id })),
  ...TEAM_MANAGEMENT_RESOURCES.map((id) => ({
    id,
    label: RESOURCE_GROUP_LABEL[id] ?? id,
    description: TEAM_GROUP_DESCRIPTION[id]
  }))
];
var actionsFor = (group) => group.actions ?? [...DEFAULT_ACTIONS];
function hasPermission(matrix, group, action) {
  const actions = matrix[group];
  if (!actions) return false;
  return actions.includes(action) || actions.includes(MANAGE_ACTION);
}
function togglePermission(matrix, group, action) {
  const current = new Set(matrix[group] ?? []);
  if (current.has(action)) current.delete(action);
  else current.add(action);
  const next = { ...matrix };
  if (current.size === 0) delete next[group];
  else next[group] = [...current];
  return next;
}
function setGroupActions(matrix, group, actions) {
  const next = { ...matrix };
  if (actions.length === 0) delete next[group];
  else next[group] = [...new Set(actions)];
  return next;
}
function countPermissions(matrix) {
  return Object.values(matrix).reduce((sum, actions) => sum + actions.length, 0);
}
function grantedGroupCount(matrix) {
  return Object.keys(matrix).length;
}
function groupFullyGranted(matrix, group) {
  const granted = matrix[group.id];
  if (!granted) return false;
  if (granted.includes(MANAGE_ACTION)) return true;
  return actionsFor(group).every((action) => granted.includes(action));
}
function matrixFromScopes(scopeNames) {
  const out = {};
  for (const scope of scopeNames) {
    const i = scope.lastIndexOf(":");
    if (i <= 0 || i === scope.length - 1) continue;
    const resource = scope.slice(0, i);
    const action = scope.slice(i + 1);
    const actions = out[resource] ??= [];
    if (!actions.includes(action)) actions.push(action);
  }
  return out;
}
function mergeMatrices(...matrices) {
  const out = {};
  for (const matrix of matrices) {
    for (const [group, actions] of Object.entries(matrix)) {
      const merged = /* @__PURE__ */ new Set([...out[group] ?? [], ...actions]);
      out[group] = merged.has(MANAGE_ACTION) ? [MANAGE_ACTION] : [...merged];
    }
  }
  return out;
}

// src/auth/abac.ts
function subjectUserId(subject) {
  return String(subject.userId ?? "");
}
function asSet(ids) {
  return new Set((ids ?? []).map((id) => String(id)));
}
function isOwner(subject, resource) {
  const uid = subjectUserId(subject);
  return uid !== "" && String(resource.ownerUserId ?? "") === uid;
}
function isExplicitlyShared(subject, resource) {
  const uid = subjectUserId(subject);
  return uid !== "" && asSet(resource.visibleToUserIds).has(uid);
}
function sharedTeamIds(subject, resource) {
  const resourceTeams = asSet(resource.visibleToTeamIds);
  return [...asSet(subject.teamIds)].filter((t) => resourceTeams.has(t));
}
function isTeamShared(subject, resource) {
  return sharedTeamIds(subject, resource).length > 0;
}
function canView(subject, resource, options = {}) {
  return options.rbacTenantAccess === true || isOwner(subject, resource) || isExplicitlyShared(subject, resource) || isTeamShared(subject, resource);
}
function canMutate(subject, resource, options = {}) {
  return canView(subject, resource, options);
}
function filterViewable(subject, resources, options = {}) {
  return [...resources].filter((r) => canView(subject, r, options));
}

export { ACTION_LABEL, ACTION_TO_SCOPE_ACTIONS, APPLICATIONS, APP_PREFETCH, APP_REGISTRY, AUTH0_TENANTS, AUTH_FIELDS, AUTH_TYPES, BUILD_TIME_POLICY_ENV_KEYS, COLLABORATIVE_RESOURCES, DEFAULT_ACTIONS, DEFAULT_CLAIMS_NAMESPACE, DEFAULT_RBAC_POLICY, DEFAULT_RESOURCE_GROUPS, DEFAULT_SCOPE_ACTIONS, EMPTY_TENANCY_NAMES, HEALTH_CHECK_SENTINEL, LOGIN_APP, MANAGE_ACTION, PROD_CUSTOM_DOMAIN, RBAC_MODEL, RESOURCE_GROUP_LABEL, RETURN_TO_PARAM, TEAM, TEAM_AUDIT, TEAM_GROUP_DESCRIPTION, TEAM_GROUP_SCOPES, TEAM_INVITES, TEAM_MANAGEMENT_RESOURCES, TEAM_MEMBERS, TEAM_ROLES, actionsGrant, applyPrefetchPlan, auth0TenantEnv, buildAuth0Config, buildPrefetchPlan, buildTimePermissionSet, canAccessApplication, canMutate, canView, captureReturnTo, claimReader, clearPolicyCache, clearRegionPin, clearScopeCatalogCache, countPermissions, createAutheEnforcer, createClaimReader, createMockToken, decodeJwtPayload, enforceFromRolePermissions, filterViewable, getAllowedApplications, getAppEntry, getApplicationType, getImpersonationState, getRegionConfig, getScopes, grantedGroupCount, groupFullyGranted, hasPermission, impersonationBanner, isAllowedReturnTo, isApplicationAllowedForType, isDevAuthEnabled, isExplicitlyShared, isImpersonating, isImpersonationExpired, isOwner, isTeamShared, isValidApplication, isValidAuthType, keyMatch, loadPermissionsView, loadPolicy, loadPolicyFromUrl, loadScopeCatalog, materializePermissionSet, matrixFromScopes, mergeMatrices, parseBuildTimePolicy, parsePolicyCsv, parseRolePermissionsArray, parseRolePermissionsPolicy, parseScopeCatalog, permissionSetFromClaims, permissionSetFromPolicy, permissionSetFromPolicyCsv, permissionSetFromRolePermissions, pinRegion, platformPreconnect, policyFromRolePermissions, readAutheClaims, readRegionPin, readReturnTo, resolveAppBasePath, resolveAppOrigin, resolveAppPrefetch, resolveAppUrl, resolveAuth0Config, resolveAuth0ConfigFromEnv, resolveBuildTimePolicyCsv, resolveLoginUrl, resolvePermissionsView, resolvePolicyUrl, resolveScopeCatalogUrl, rolePermissionsFromPolicy, rolePermissionsGrant, rolePermissionsMapFromClaim, scopeActionsFor, scopesFromPolicy, scopesFromRolePermissionsClaim, setGroupActions, sharedTeamIds, switchOrganization, switchTenant, teamScopeFor, tenancyNamesFromClaims, tenantContextFromClaims, tenantStoragePrefix, toScopeActions, togglePermission };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map